import { fromLonLat } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import TileState from 'ol/TileState';
import { boundingExtent } from 'ol/extent';

import { LayerManager } from 'models';
import { getTileGrid } from 'utils';
import { SA_BOUNDS, GEOSERVER_URL, MAP_PROJECTION } from 'config';

import { AbstractLayer, AbstractLayerProps, LayerProps } from './AbstractLayer';

export interface Props extends AbstractLayerProps {
    config: LayerManager.LayerConfig;
    url?: string;
}

/**
 * The WmsTileLayer is an OpenLayers TileLayer intended to render
 * raster data from a WMS service and load everything as tiles
 * so that caching can be made the most of.
 */
export class WmsTileLayer extends AbstractLayer<TileLayer<TileWMS>> {
    private date?: Date | null;

    private issueTime?: Date | null;

    private source: TileWMS | null;

    public url: string | null;

    constructor({ config, auth, opacity, visible, zIndex, url }: Props) {
        super({ config, opacity, visible, zIndex, auth });
        this.url = url ?? `${GEOSERVER_URL}/ows`;
        this.source = this.buildSource();
    }

    initializeLayer(props: LayerProps) {
        return new TileLayer({
            ...props,
            source: this.source || undefined,
        });
    }

    applyAuthentication() {
        if (this.auth && this.source) {
            this.source.setTileLoadFunction((tile, src) => {
                const xhr = new XMLHttpRequest();
                xhr.responseType = 'blob';

                // Need it like this for the 'this' variable to work

                xhr.addEventListener('loadend', function () {
                    const data = this.response;
                    if (data !== undefined) {
                        try {
                            // @ts-ignore

                            tile.getImage().src = URL.createObjectURL(data);
                        } catch (e) {
                            console.error(e);
                            tile.setState(TileState.ERROR);
                        }
                    } else {
                        tile.setState(TileState.ERROR);
                    }
                });
                xhr.addEventListener('error', () => {
                    tile.setState(TileState.ERROR);
                });
                xhr.open('GET', src);

                if (this.auth)
                    xhr.setRequestHeader(
                        'Authorization',
                        `${this.auth.tokenType} ${this.auth.accessToken}`,
                    );

                xhr.send();
            });
        }
    }

    private buildSource() {
        if (this.url == null || this.config == null) return null;

        const SRS = MAP_PROJECTION.getCode();

        const coords = SA_BOUNDS;
        const stateExtent = boundingExtent([
            fromLonLat([coords.minLong, coords.minLat], SRS),
            fromLonLat([coords.maxLong, coords.minLat], SRS),
            fromLonLat([coords.maxLong, coords.maxLat], SRS),
            fromLonLat([coords.minLong, coords.minLat], SRS),
        ]);

        const source = new TileWMS({
            crossOrigin: 'anonymous',
            url: this.url,
            params: {
                LAYERS: this.config.serviceName,
                STYLES: this.config.wmsStyles ?? '',
                TIME: this.date?.toISOString(),
                VERSION: '1.1.1',
                TILED: true,
                SRS,
            },
            tileGrid: getTileGrid(SRS, stateExtent),
        });
        // Add CQL Filter required for filtering the data based on BMC
        if (this.filter && this.filter != '') {
            source.updateParams({ CQL_FILTER: this.filter });
        }

        this.source?.dispose();
        this.source = source;
        if (this.layer) this.layer?.setSource(source);

        this.applyAuthentication();
        return source;
    }

    updateDate(date?: Date | null, issueTime?: Date | null) {
        if (date !== null) this.date = date;
        if (issueTime !== null) this.issueTime = issueTime;

        if (this.source) {
            this.source.updateParams({
                ...this.source.getParams(),
                TIME: this.date?.toISOString(),
                DIM_ISSUE_TIME: this.issueTime?.toISOString(),
            });
        }
    }

    setFilter(filter: string) {
        this.filter = filter;
        // Add CQL Filter required for filtering the data based on BMC
        if (this.source) {
            this.source.updateParams({ CQL_FILTER: this.filter });
        }
        this.source?.refresh();
    }

    refresh() {
        // Added timestamp parameter for force refreshing the layers instead of using cache
        this.source?.updateParams({ TIMESTAMP: Date.now() });
        this.source?.refresh();
    }
}

export default WmsTileLayer;
